<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateProduct',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    preventDefaultOnClose: {
      type: Boolean,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const item = ref(null);
  const formName = ref('meter');
  const formId = ref(null);
  const resource = ref(null);
  const editor = ref(null);
  const container = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    item: item.value,
    formName: formName.value,
    formId: formId.value,
    resource: resource.value,
    editor: editor.value,
    container: container.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = async () => {
    return $fn.value.schemaDefaults('CreateProduct', { sync: true });
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_resource = async () => {
    return new $fn.value.Resource(item.value);
  };
  const $$load_editor = async () => {
    return resource.value.editor();
  };
  const $$load_container = async () => {
    return {
      self: $fn.value.urlFor('create_product'),
      object: 'list',
      data: [],
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'resource', dependsOn: ['item'] },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'container' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      item: $$load_item,
      formId: $$load_formId,
      resource: $$load_resource,
      editor: $$load_editor,
      container: $$load_container,
    },
    ux: {},
    dataRefs: {
      item,
      formName,
      formId,
      resource,
      editor,
      container,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    return $fn.value.pgettext('Product', 'Product');
  });
  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Product', 'New product') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.preventDefaultOnClose,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('preventDefaultOnClose');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_mwq98dg50_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_setData_wjrjo4ife_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: editor.value.getData(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_closeComponent_wj4132xj6_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_navigate_omoufvpmd_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.product',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const inst_evh_1040167445267876_created_zgdg58ul1_getWhen = async (
    $event
  ) => {
    return !props.preventDefaultOnClose;
  };
  const act_1040167445267876_reloadSlickgrid_3l46osac4_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'products',
    };
  };
  const inst_evh_1040167445267876_created_fyq2x20vx_getWhen = async (
    $event
  ) => {
    return !props.preventDefaultOnClose;
  };
  const act_1040167445267876_rest_f9igbudu2_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_mwq98dg50 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_mwq98dg50_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ecqpwd0ig =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_mwq98dg50,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ecqpwd0ig],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_x9h63jutn =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_v9b0wv7ix =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_x9h63jutn,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_v9b0wv7ix],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_ghf6vhol8 =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_upraas88a =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_ghf6vhol8,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_upraas88a],
    event: 'close',
  });
  const act_1040167445267876_setData_wjrjo4ife = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_wjrjo4ife_getActionArgs,
  });
  const inst_evh_1040167445267876_before_v4gkdp20x =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_wjrjo4ife,
      event: 'before',
      displayName: 'setData',
    });
  const act_1040167445267876_closeComponent_wj4132xj6 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_wj4132xj6_getActionArgs,
    });
  const inst_evh_1040167445267876_created_6tmhg0cs4 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_wj4132xj6,
      event: 'created',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_navigate_omoufvpmd =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_omoufvpmd_getActionArgs,
    });
  const inst_evh_1040167445267876_created_zgdg58ul1 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_omoufvpmd,
      event: 'created',
      when: inst_evh_1040167445267876_created_zgdg58ul1_getWhen,
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadSlickgrid_3l46osac4 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_3l46osac4_getActionArgs,
    });
  const inst_evh_1040167445267876_created_fyq2x20vx =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_3l46osac4,
      event: 'created',
      when: inst_evh_1040167445267876_created_fyq2x20vx_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_rest_f9igbudu2 = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_f9igbudu2_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_before_v4gkdp20x,
      inst_evh_1040167445267876_created_6tmhg0cs4,
      inst_evh_1040167445267876_created_zgdg58ul1,
      inst_evh_1040167445267876_created_fyq2x20vx,
    ],
  });
  const inst_evh_1040167445267876_submit_fgkiwmr0c =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_f9igbudu2,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_fgkiwmr0c],
    event: 'submit',
  });
  const act_2167826738365259_closeComponent_vdqu7ysm6 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2167826738365259_clickPreventStop_ykf3xd877 =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeComponent_vdqu7ysm6,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_ykf3xd877],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_ecqpwd0ig,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_v9b0wv7ix,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_upraas88a,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_before_v4gkdp20x,
    inst_evh_1040167445267876_created_6tmhg0cs4,
    inst_evh_1040167445267876_created_zgdg58ul1,
    inst_evh_1040167445267876_created_fyq2x20vx,
    inst_evh_1040167445267876_submit_fgkiwmr0c,
    create: inst_evh_1040167445267876_submit_fgkiwmr0c,
    inst_evh_1040167445267876_submit,
    inst_evh_2167826738365259_clickPreventStop_ykf3xd877,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_ecqpwd0ig = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ecqpwd0ig.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_v9b0wv7ix = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_v9b0wv7ix.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_upraas88a = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_upraas88a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_before_v4gkdp20x = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_before_v4gkdp20x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_created_6tmhg0cs4 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_created_6tmhg0cs4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_created_zgdg58ul1 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_created_zgdg58ul1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_created_fyq2x20vx = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_created_fyq2x20vx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_fgkiwmr0c = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_fgkiwmr0c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_ykf3xd877 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_ykf3xd877.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateProduct"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :dialogName="resourceTitle"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateProduct"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetProductGeneral :product="editor"> </FieldsetProductGeneral>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpBox alignItems="center">
          <ItpFormGroup :label-cols-md="4" name="sync">
            <ItpFormCheckbox
              name="sync"
              :label="pgettext('Product', 'IntegroRent sync')"
              v-model="editor.sync"
              :value="editor.sync"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (editor.sync =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
        </ItpBox>
        <ItpButton
          :text="pgettext('Button', 'Save')"
          type="submit"
          :form="forms[formName]"
          :spinning="actions.create.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
